import { useRef, useEffect, useState } from 'react';

// How long the last caption line lingers after the source text clears (ms).
const LINGER_MS = 2500;

export const CaptionBox = ({
  text,
  show,
  tone = 'assistant',
}: {
  text: string;
  show: boolean;
  tone?: 'assistant' | 'user';
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const lingerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Displayed text: the live text when flowing, the last non-empty text during linger.
  const [displayed, setDisplayed] = useState(text);
  const [lingering, setLingering] = useState(false);

  useEffect(() => {
    if (text.trim()) {
      // Live text is flowing — cancel any pending linger timer.
      if (lingerTimer.current) clearTimeout(lingerTimer.current);
      setDisplayed(text);
      setLingering(false);
    } else if (displayed.trim()) {
      // Text just cleared but we have a previous value — linger briefly.
      setLingering(true);
      lingerTimer.current = setTimeout(() => {
        setLingering(false);
        setDisplayed('');
      }, LINGER_MS);
    }
    return () => { if (lingerTimer.current) clearTimeout(lingerTimer.current); };
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = show && !!(lingering ? displayed.trim() : text.trim());

  useEffect(() => {
    if (open && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [displayed, open]);

  return (
    <div
      className={`flex-shrink-0 overflow-hidden px-4 transition-all duration-300 ease-in-out ${
        open ? 'max-h-32 opacity-100 mb-2' : 'max-h-0 opacity-0 mb-0'
      }`}
      aria-hidden={!open}
    >
      <div
        ref={ref}
        className={`max-h-28 overflow-y-auto no-scrollbar rounded-xl border border-ui-border px-4 py-3 bg-ui-subtle transition-opacity duration-500 ${lingering ? 'opacity-50' : 'opacity-100'}`}
      >
        <p className={`text-sm leading-snug text-ui-text ${tone === 'user' ? 'font-medium' : ''}`}>
          {lingering ? displayed : text}
        </p>
      </div>
    </div>
  );
};

export default CaptionBox;
